/**
 * Mobile UI Handlers - Manages mobile-specific UI state for hangout
 * Extracted from message-utils.js for separation of concerns
 */

import { $, $$, addClass, removeClass } from '../../../core/utils/dom.js';

export const isMobilePortrait = () => window.innerWidth <= 1024 && window.innerHeight > window.innerWidth;

/**
 * Mark a character as currently speaking (mobile only)
 * Adds currently-speaking class and handles user positioning preservation
 */
export const markCharacterSpeaking = (characterIndex, characterId) => {
    if (!isMobilePortrait()) return;

    // Prefer multi-character containers, but fallback to the single-character container.
    const characterContainer =
        $(`[class*="character-image-"][data-character-id="${String(characterId)}"]`) ||
        $(`.character-image-${characterIndex}`) ||
        $('.character-image:not([class*="character-image-"])') ||
        $('.character-image');
    if (!characterContainer) {
        console.warn('Could not find character container for index:', characterIndex, 'characterId:', characterId);
        return;
    }

    // Add to new character FIRST to prevent flash when transitioning
    addClass(characterContainer, 'currently-speaking');
    
    // Then remove from all OTHER characters
    $$('.currently-speaking').forEach(el => {
        if (el !== characterContainer) {
            removeClass(el, 'currently-speaking');
        }
    });

    // Preserve user positioning if image has been dragged/zoomed
    const media = characterContainer.querySelector('img, video');
    if (media && (media.style.position === 'fixed' || (media.style.transform && media.style.transform !== 'scale(1)'))) {
        addClass(characterContainer, 'user-positioned');
    }
};

export const isSingleCharacterContainerMode = () => {
    const multiCharContainers = document.querySelectorAll('[class*="character-image-"]');
    const singleContainer =
        document.querySelector('.character-image:not([class*="character-image-"])') ||
        document.querySelector('.character-image');
    return !!singleContainer && multiCharContainers.length === 0;
};

export const clearConversationSpeakingClasses = () => {
    const hangoutContainer = $('#hangout-container');
    if (hangoutContainer) {
        removeClass(hangoutContainer, 'conversation-active');
        removeClass(hangoutContainer, 'single-character-conversation');
    }
    $$('.currently-speaking').forEach((el) => removeClass(el, 'currently-speaking'));
};

/**
 * Activate conversation mode (mobile only)
 * Hides non-speaking characters and shows message canvas
 */
export const activateConversationMode = async (characters, displayManager) => {
    if (!isMobilePortrait()) return;

    const hangoutContainer = $('#hangout-container');
    if (!hangoutContainer) {
        console.warn('Could not find #hangout-container');
        return;
    }

    // Avoid toggling conversation-active for single-character DOM mode; it is only needed
    // to hide non-speaking characters in multi-character mobile portrait.
    const isSingleCharacter = characters?.length === 1 || isSingleCharacterContainerMode();

    if (!isSingleCharacter) {
        addClass(hangoutContainer, 'conversation-active');
    } else {
        addClass(hangoutContainer, 'single-character-conversation');
    }

    // Show message canvas if display manager is available
    if (displayManager?.isAvailable?.()) {
        displayManager.showCanvas();
    }
};

/**
 * Handle orientation change - show all characters when switching to landscape
 * In portrait, only the speaking character is shown. In landscape, all characters should be visible.
 */
export const handleOrientationChange = async () => {
    const isLandscape = window.innerWidth > window.innerHeight;
    const isMobileTablet = window.innerWidth <= 1024;
    
    if (isLandscape && isMobileTablet) {
        // Switched to landscape on mobile/tablet - show all characters
        console.log('📱 Orientation change to landscape - showing all characters');
        
        clearConversationSpeakingClasses();
        
        // Ensure all character containers are visible
        $$('.character-image, [class*="character-image-"]').forEach(el => {
            el.style.opacity = '';
            el.style.pointerEvents = '';
        });
    }

    // Refresh sprite styles for the new orientation (portrait or landscape)
    // This fixes sprites appearing tiny after switching from landscape to portrait
    try {
        const { refreshSpriteStylesForOrientation } = await import('./character-manager.js');
        refreshSpriteStylesForOrientation();
    } catch (error) {
        console.warn('📱 Failed to refresh sprite styles for orientation:', error);
    }
};

/**
 * Initialize orientation change listener
 * Call this once during hangout initialization
 */
export const initOrientationChangeListener = () => {
    // Use both resize and orientationchange for better coverage
    let resizeTimeout;
    const debouncedHandler = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(handleOrientationChange, 300);
    };
    
    window.addEventListener('resize', debouncedHandler);
    window.addEventListener('orientationchange', () => {
        // Orientation change event may fire before dimensions update
        setTimeout(handleOrientationChange, 500);
    });
    
    console.log('📱 Orientation change listener initialized');
};
